<template>
  <div v-if="order">
    <div class="topbar">
      <div class="brand" @click="$router.back()">&#8249; 订单详情</div>
    </div>
    <div class="content">
      <div class="card">
        <div class="row">
          <div style="font-weight: 600; font-size: 17px">{{ order.slotTitle }}</div>
          <span class="status" :class="statusKey">{{ statusText }}</span>
        </div>
        <div class="row" style="margin-top: 8px">
          <div class="muted">科研专家</div>
          <div>{{ order.expertName }}</div>
        </div>
        <div class="row">
          <div class="muted">金额</div>
          <div class="price">¥{{ (order.orderTotalAmount / 100).toLocaleString("zh-CN") }}</div>
        </div>
        <div class="row" v-if="order.consultNote && !canEditExtra">
          <div class="muted">预咨询</div>
          <div style="max-width: 60%; text-align: right">{{ order.consultNote }}</div>
        </div>
        <div class="row" v-if="order.manuscriptUrl && !canEditExtra">
          <div class="muted">上传稿件</div>
          <div style="max-width: 60%; text-align: right">
            <a
              :href="order.manuscriptUrl"
              target="_blank"
              style="color: var(--color-link); word-break: break-all"
              >查看稿件</a
            >
          </div>
        </div>
        <div class="order-meta">
          <div>订单号：{{ order.orderNo }}</div>
          <div>生成时间：{{ order.createTime }}</div>
          <div v-if="order.orderPayTime">支付时间：{{ order.orderPayTime }}</div>
        </div>
      </div>

      <div v-if="order.orderStatus === 0" class="payment-section">
        <div class="payment-title">选择支付方式</div>
        <button
          v-for="channel in paymentChannels"
          :key="channel.channel"
          type="button"
          class="payment-option"
          :class="{ selected: selectedPaymentChannel === channel.channel }"
          @click="selectedPaymentChannel = channel.channel"
        >
          <span class="pay-icon" :class="channel.channel === 'WECHAT_JSAPI' ? 'wechat' : 'alipay'">
            {{ channel.channel === "WECHAT_JSAPI" ? "微" : "支" }}
          </span>
          <span>{{ channel.channelName }}</span>
          <span class="payment-radio" :class="{ checked: selectedPaymentChannel === channel.channel }">
            <span></span>
          </span>
        </button>
        <div v-if="channelsLoading" class="payment-empty">支付方式加载中...</div>
        <div v-else-if="!paymentChannels.length" class="payment-empty">暂无可用支付方式</div>
      </div>

      <button v-if="order.orderStatus === 0" class="btn" :disabled="paying || channelsLoading || !selectedPaymentChannel" @click="pay" v-track="'NRCU008|NRCU008-002|点击去支付'">
        {{ paying ? "支付发起中..." : "去支付 ¥" + (order.orderTotalAmount / 100).toLocaleString("zh-CN") }}
      </button>
      <div class="muted" style="text-align: center; margin-top: 10px" v-if="order.orderStatus === 0">
        本平台提供科研咨询服务，非医疗类目
      </div>

      <button v-if="canEditExtra" class="btn consult-btn" @click="openConsult">
        {{ order.consultNote || order.manuscriptUrl ? "修改预咨询信息" : "填写预咨询信息" }}
      </button>

      <div class="card" v-if="order.deliveryContent">
        <div style="font-weight: 600; margin-bottom: 6px">交付内容</div>
        <div style="font-size: 14px; white-space: pre-wrap">{{ order.deliveryContent }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { orderDetail, orderPay, paymentChannels } from "@/api/nrc.js";
import { wechatPay } from "@/utils/payMethod.js";
import { dataGather } from "@/utils/logTracking.js";
import {
  clearPendingAction,
  ensureOpenId,
  getOpenId,
  getPendingAction,
} from "@/utils/wxAuth.js";
import { Toast } from "vant";

const STATUS_KEY_MAP = {
  0: "pending",
  1: "paid",
  2: "paid",
  3: "delivered",
  4: "reviewed",
  5: "refunded",
  98: "closed",
  99: "closed",
};
const STATUS_TEXT_MAP = {
  pending: "待支付",
  paid: "待交付",
  delivered: "已交付",
  reviewed: "已完成",
  closed: "已关闭",
  refunded: "已退款",
};

export default {
  name: "OrderDetail",
  data() {
    return {
      order: null,
      paying: false,
      paymentChannels: [],
      selectedPaymentChannel: "",
      channelsLoading: false,
      resumePaymentChecked: false,
    };
  },
  computed: {
    statusKey() {
      return STATUS_KEY_MAP[this.order.orderStatus] || "closed";
    },
    statusText() {
      return STATUS_TEXT_MAP[this.statusKey] || "--";
    },
    // 跟后端规则对应：只有"已支付、还没开始交付"(orderStatus===1)这段时间允许补填/修改
    canEditExtra() {
      return this.order.orderStatus === 1;
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      orderDetail(this.$route.params.id).then((r) => {
        this.order = r;
        if (r.orderStatus === 0) {
          this.loadPaymentChannels();
        }
      });
    },
    loadPaymentChannels() {
      this.channelsLoading = true;
      paymentChannels()
        .then((data) => {
          this.paymentChannels = data.availableChannels || [];
          const stillAvailable = this.paymentChannels.some(
            (item) => item.channel === this.selectedPaymentChannel
          );
          if (!stillAvailable) {
            this.selectedPaymentChannel = this.paymentChannels.length
              ? this.paymentChannels[0].channel
              : "";
          }
        })
        .finally(() => {
          this.channelsLoading = false;
          this.resumeWechatPayment();
        });
    },
    resumeWechatPayment() {
      if (this.resumePaymentChecked) return;
      this.resumePaymentChecked = true;

      const pending = getPendingAction();
      if (!pending || pending.type !== "WECHAT_ORDER_PAY") return;
      if (!this.order || String(pending.orderId) !== String(this.order.id)) return;
      if (this.order.orderStatus !== 0 || !getOpenId()) {
        clearPendingAction();
        return;
      }
      const channelAvailable = this.paymentChannels.some(
        (item) => item.channel === pending.channel
      );
      if (!channelAvailable) {
        clearPendingAction();
        Toast("原支付方式当前不可用，请重新选择");
        return;
      }

      // 授权返回后只续接一次，调用支付接口前立即清除，避免刷新或返回页面时重复拉起。
      clearPendingAction();
      this.selectedPaymentChannel = pending.channel;
      Toast.loading({ message: "微信授权完成，正在发起支付...", forbidClick: true, duration: 0 });
      this.startPay(pending.channel);
    },
    pay() {
      if (!this.selectedPaymentChannel) {
        Toast("请选择支付方式");
        return;
      }
      this.startPay(this.selectedPaymentChannel);
    },
    startPay(channel) {
      if (channel === "ALIPAY_WAP") {
        this.startAlipay();
        return;
      }
      ensureOpenId(
        this.$route.fullPath,
        () => {
          this.paying = true;
          orderPay({ orderId: this.order.id, channel, openId: getOpenId() })
            .then((payResult) => {
              Toast.clear();
              wechatPay(
                payResult.wechatJsApi,
                () => {
                  Toast.success("支付成功");
                  dataGather("NRCU008", "NRCU008-003", { eventName: "支付成功", orderId: this.order.id });
                  this.$router.replace(`/order/${this.order.id}/consult?fromPay=1`);
                },
                () => {
                  Toast("支付未完成");
                }
              );
            })
            .finally(() => {
              Toast.clear();
              this.paying = false;
            });
        },
        { type: "WECHAT_ORDER_PAY", orderId: this.order.id, channel }
      );
    },
    startAlipay() {
      this.paying = true;
      const returnUrl = `${window.location.origin}${window.location.pathname}#/order/${this.order.id}`;
      orderPay({
        orderId: this.order.id,
        channel: "ALIPAY_WAP",
        returnUrl,
      })
        .then((payResult) => {
          const body = payResult && payResult.alipayH5 && payResult.alipayH5.body;
          if (!body) {
            Toast("支付宝支付参数为空");
            return;
          }
          document.open();
          document.write(body);
          document.close();
        })
        .finally(() => {
          this.paying = false;
        });
    },
    openConsult() {
      this.$router.push(`/order/${this.order.id}/consult`);
    },
  },
};
</script>

<style scoped>
.payment-section { margin: 14px 0; overflow: hidden; border-radius: 12px; background: #fff; box-shadow: 0 2px 8px rgba(15, 23, 42, .06); }
.payment-title { padding: 15px 16px 10px; color: #777; font-size: 14px; }
.payment-option { width: 100%; height: 62px; padding: 0 16px; border: 0; border-top: 1px solid #f0f0f0; background: #fff; display: flex; align-items: center; color: #222; font-size: 16px; text-align: left; }
.payment-option.selected { background: #fbfeff; }
.pay-icon { width: 34px; height: 34px; margin-right: 12px; border-radius: 8px; color: #fff; line-height: 34px; text-align: center; font-weight: 600; }
.pay-icon.wechat { background: #07c160; }
.pay-icon.alipay { background: #1677ff; }
.payment-radio { width: 22px; height: 22px; margin-left: auto; box-sizing: border-box; border: 2px solid #c9cdd2; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.payment-radio.checked { border-color: var(--color-primary); background: var(--color-primary); }
.payment-radio.checked span { width: 8px; height: 4px; margin-top: -2px; border-left: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(-45deg); }
.payment-empty { padding: 18px 16px; border-top: 1px solid #f0f0f0; color: #999; text-align: center; font-size: 14px; }
.consult-btn { margin-top: 14px; }
</style>
